import _regeneratorRuntime from "/app/node_modules/@babel/runtime/helpers/esm/regeneratorRuntime.js";
import _asyncToGenerator from "/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/app/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _objectSpread from "/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
var _excluded = ["lockScroll"],
  _excluded2 = ["listRef", "overflowRef", "onFallbackChange", "offset", "index", "minItemsVisible", "referenceOverflowThreshold"];
import { useFloating as useFloating$1, offset, detectOverflow, getOverflowAncestors } from '@floating-ui/react-dom';
export * from '@floating-ui/react-dom';
import * as React from 'react';
import { useLayoutEffect, useEffect, useRef } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { hideOthers } from 'aria-hidden';
var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;
function createPubSub() {
  var map = new Map();
  return {
    emit: function emit(event, data) {
      var _map$get;
      (_map$get = map.get(event)) == null ? void 0 : _map$get.forEach(function (handler) {
        return handler(data);
      });
    },
    on: function on(event, listener) {
      map.set(event, [].concat(_toConsumableArray(map.get(event) || []), [listener]));
    },
    off: function off(event, listener) {
      map.set(event, (map.get(event) || []).filter(function (l) {
        return l !== listener;
      }));
    }
  };
}
var serverHandoffComplete = false;
var count = 0;
var genId = function genId() {
  return "floating-ui-" + count++;
};
function useFloatingId() {
  var _React$useState = React.useState(function () {
      return serverHandoffComplete ? genId() : undefined;
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    id = _React$useState2[0],
    setId = _React$useState2[1];
  index(function () {
    if (id == null) {
      setId(genId());
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(function () {
    if (!serverHandoffComplete) {
      serverHandoffComplete = true;
    }
  }, []);
  return id;
} // `toString()` prevents bundlers from trying to `import { useId } from 'react'`

var useReactId = React[/*#__PURE__*/'useId'.toString()];
/**
 * Uses React 18's built-in `useId()` when available, or falls back to a
 * slightly less performant (requiring a double render) implementation for
 * earlier React versions.
 * @see https://floating-ui.com/docs/useId
 */

var useId = useReactId != null ? useReactId : useFloatingId;
var FloatingNodeContext = /*#__PURE__*/React.createContext(null);
var FloatingTreeContext = /*#__PURE__*/React.createContext(null);
var useFloatingParentNodeId = function useFloatingParentNodeId() {
  var _React$useContext$id, _React$useContext;
  return (_React$useContext$id = (_React$useContext = React.useContext(FloatingNodeContext)) == null ? void 0 : _React$useContext.id) != null ? _React$useContext$id : null;
};
var useFloatingTree = function useFloatingTree() {
  return React.useContext(FloatingTreeContext);
};
/**
 * Registers a node into the floating tree, returning its id.
 */

var useFloatingNodeId = function useFloatingNodeId() {
  var id = useId();
  var tree = useFloatingTree();
  var parentId = useFloatingParentNodeId();
  index(function () {
    var node = {
      id: id,
      parentId: parentId
    };
    tree == null ? void 0 : tree.addNode(node);
    return function () {
      tree == null ? void 0 : tree.removeNode(node);
    };
  }, [tree, id, parentId]);
  return id;
};
/**
 * Provides parent node context for nested floating elements.
 * @see https://floating-ui.com/docs/FloatingTree
 */

var FloatingNode = function FloatingNode(_ref) {
  var children = _ref.children,
    id = _ref.id;
  var parentId = useFloatingParentNodeId();
  return /*#__PURE__*/React.createElement(FloatingNodeContext.Provider, {
    value: React.useMemo(function () {
      return {
        id: id,
        parentId: parentId
      };
    }, [id, parentId])
  }, children);
};
/**
 * Provides context for nested floating elements when they are not children of
 * each other on the DOM (i.e. portalled to a common node, rather than their
 * respective parent).
 * @see https://floating-ui.com/docs/FloatingTree
 */

var FloatingTree = function FloatingTree(_ref2) {
  var children = _ref2.children;
  var nodesRef = React.useRef([]);
  var addNode = React.useCallback(function (node) {
    nodesRef.current = [].concat(_toConsumableArray(nodesRef.current), [node]);
  }, []);
  var removeNode = React.useCallback(function (node) {
    nodesRef.current = nodesRef.current.filter(function (n) {
      return n !== node;
    });
  }, []);
  var events = React.useState(function () {
    return createPubSub();
  })[0];
  return /*#__PURE__*/React.createElement(FloatingTreeContext.Provider, {
    value: React.useMemo(function () {
      return {
        nodesRef: nodesRef,
        addNode: addNode,
        removeNode: removeNode,
        events: events
      };
    }, [nodesRef, addNode, removeNode, events])
  }, children);
};
function getDocument(floating) {
  var _floating$ownerDocume;
  return (_floating$ownerDocume = floating == null ? void 0 : floating.ownerDocument) != null ? _floating$ownerDocume : document;
}
function getWindow(value) {
  var _getDocument$defaultV;
  return (_getDocument$defaultV = getDocument(value).defaultView) != null ? _getDocument$defaultV : window;
}
function isElement(value) {
  return value ? value instanceof getWindow(value).Element : false;
}
function isHTMLElement(value) {
  return value ? value instanceof getWindow(value).HTMLElement : false;
}

// `toString()` prevents bundlers from trying to `import { useInsertionEffect } from 'react'`
var useInsertionEffect = React[/*#__PURE__*/'useInsertionEffect'.toString()];
function useEvent(callback) {
  var ref = React.useRef(function () {
    if (process.env.NODE_ENV !== "production") {
      throw new Error('Cannot call an event handler while rendering.');
    }
  });
  if (useInsertionEffect) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInsertionEffect(function () {
      ref.current = callback;
    });
  } else {
    ref.current = callback;
  }
  return React.useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return ref.current == null ? void 0 : ref.current.apply(ref, args);
  }, []);
}
function useFloating(_temp) {
  var _ref5 = _temp === void 0 ? {} : _temp,
    _ref5$open = _ref5.open,
    open = _ref5$open === void 0 ? false : _ref5$open,
    unstable_onOpenChange = _ref5.onOpenChange,
    whileElementsMounted = _ref5.whileElementsMounted,
    placement = _ref5.placement,
    middleware = _ref5.middleware,
    strategy = _ref5.strategy,
    nodeId = _ref5.nodeId;
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    domReference = _React$useState4[0],
    setDomReference = _React$useState4[1];
  var tree = useFloatingTree();
  var domReferenceRef = React.useRef(null);
  var dataRef = React.useRef({});
  var events = React.useState(function () {
    return createPubSub();
  })[0];
  var position = useFloating$1({
    placement: placement,
    middleware: middleware,
    strategy: strategy,
    whileElementsMounted: whileElementsMounted
  });
  var onOpenChange = useEvent(unstable_onOpenChange);
  var refs = React.useMemo(function () {
    return _objectSpread(_objectSpread({}, position.refs), {}, {
      domReference: domReferenceRef
    });
  }, [position.refs]);
  var context = React.useMemo(function () {
    return _objectSpread(_objectSpread({}, position), {}, {
      refs: refs,
      dataRef: dataRef,
      nodeId: nodeId,
      events: events,
      open: open,
      onOpenChange: onOpenChange,
      _: {
        domReference: domReference
      }
    });
  }, [position, nodeId, events, open, onOpenChange, refs, domReference]);
  index(function () {
    var node = tree == null ? void 0 : tree.nodesRef.current.find(function (node) {
      return node.id === nodeId;
    });
    if (node) {
      node.context = context;
    }
  });
  var reference = position.reference;
  var setReference = React.useCallback(function (node) {
    if (isElement(node) || node === null) {
      context.refs.domReference.current = node;
      setDomReference(node);
    }
    reference(node);
  }, [reference, context.refs]);
  return React.useMemo(function () {
    return _objectSpread(_objectSpread({}, position), {}, {
      context: context,
      refs: refs,
      reference: setReference
    });
  }, [position, refs, context, setReference]);
}
function mergeProps(userProps, propsList, elementKey) {
  var map = new Map();
  return _objectSpread(_objectSpread(_objectSpread({}, elementKey === 'floating' && {
    tabIndex: -1
  }), userProps), propsList.map(function (value) {
    return value ? value[elementKey] : null;
  }).concat(userProps).reduce(function (acc, props) {
    if (!props) {
      return acc;
    }
    Object.entries(props).forEach(function (_ref) {
      var _ref6 = _slicedToArray(_ref, 2),
        key = _ref6[0],
        value = _ref6[1];
      if (key.indexOf('on') === 0) {
        if (!map.has(key)) {
          map.set(key, []);
        }
        if (typeof value === 'function') {
          var _map$get;
          (_map$get = map.get(key)) == null ? void 0 : _map$get.push(value);
          acc[key] = function () {
            var _map$get2;
            for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
            }
            (_map$get2 = map.get(key)) == null ? void 0 : _map$get2.forEach(function (fn) {
              return fn.apply(void 0, args);
            });
          };
        }
      } else {
        acc[key] = value;
      }
    });
    return acc;
  }, {}));
}
var useInteractions = function useInteractions(propsList) {
  if (propsList === void 0) {
    propsList = [];
  }

  // The dependencies are a dynamic array, so we can't use the linter's
  // suggestion to add it to the deps array.
  var deps = propsList;
  var getReferenceProps = React.useCallback(function (userProps) {
    return mergeProps(userProps, propsList, 'reference');
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  var getFloatingProps = React.useCallback(function (userProps) {
    return mergeProps(userProps, propsList, 'floating');
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  var getItemProps = React.useCallback(function (userProps) {
    return mergeProps(userProps, propsList, 'item');
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  return React.useMemo(function () {
    return {
      getReferenceProps: getReferenceProps,
      getFloatingProps: getFloatingProps,
      getItemProps: getItemProps
    };
  }, [getReferenceProps, getFloatingProps, getItemProps]);
};
function getChildren(nodes, id) {
  var _nodes$filter;
  var allChildren = (_nodes$filter = nodes.filter(function (node) {
    var _node$context;
    return node.parentId === id && ((_node$context = node.context) == null ? void 0 : _node$context.open);
  })) != null ? _nodes$filter : [];
  var currentChildren = allChildren;
  while (currentChildren.length) {
    var _nodes$filter2;
    currentChildren = (_nodes$filter2 = nodes.filter(function (node) {
      var _currentChildren;
      return (_currentChildren = currentChildren) == null ? void 0 : _currentChildren.some(function (n) {
        var _node$context2;
        return node.parentId === n.id && ((_node$context2 = node.context) == null ? void 0 : _node$context2.open);
      });
    })) != null ? _nodes$filter2 : [];
    allChildren = allChildren.concat(currentChildren);
  }
  return allChildren;
}
function isPointInPolygon(point, polygon) {
  var _point = _slicedToArray(point, 2),
    x = _point[0],
    y = _point[1];
  var isInside = false;
  var length = polygon.length;
  for (var i = 0, j = length - 1; i < length; j = i++) {
    var _ref7 = polygon[i] || [0, 0],
      _ref8 = _slicedToArray(_ref7, 2),
      xi = _ref8[0],
      yi = _ref8[1];
    var _ref9 = polygon[j] || [0, 0],
      _ref10 = _slicedToArray(_ref9, 2),
      xj = _ref10[0],
      yj = _ref10[1];
    var intersect = yi >= y !== yj >= y && x <= (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) {
      isInside = !isInside;
    }
  }
  return isInside;
}
function safePolygon(_temp) {
  var _ref11 = _temp === void 0 ? {} : _temp,
    _ref11$restMs = _ref11.restMs,
    restMs = _ref11$restMs === void 0 ? 0 : _ref11$restMs,
    _ref11$buffer = _ref11.buffer,
    buffer = _ref11$buffer === void 0 ? 0.5 : _ref11$buffer,
    _ref11$blockPointerEv = _ref11.blockPointerEvents,
    blockPointerEvents = _ref11$blockPointerEv === void 0 ? true : _ref11$blockPointerEv,
    _ref11$debug = _ref11.debug,
    debug = _ref11$debug === void 0 ? null : _ref11$debug;
  var timeoutId;
  var polygonIsDestroyed = false;
  var fn = function fn(_ref) {
    var x = _ref.x,
      y = _ref.y,
      placement = _ref.placement,
      refs = _ref.refs,
      onClose = _ref.onClose,
      nodeId = _ref.nodeId,
      tree = _ref.tree,
      _ref$leave = _ref.leave,
      leave = _ref$leave === void 0 ? false : _ref$leave;
    return function onPointerMove(event) {
      var _refs$domReference$cu, _refs$floating$curren, _refs$floating$curren2;
      clearTimeout(timeoutId);
      function close() {
        clearTimeout(timeoutId);
        onClose();
      }
      if (event.pointerType && event.pointerType !== 'mouse') {
        return;
      }
      var clientX = event.clientX,
        clientY = event.clientY;
      var target = 'composedPath' in event ? event.composedPath()[0] : event.target;
      var targetNode = target; // If the pointer is over the reference, there is no need to run the logic

      if (event.type === 'pointermove' && (_refs$domReference$cu = refs.domReference.current) != null && _refs$domReference$cu.contains(targetNode)) {
        return;
      } // Prevent overlapping floating element from being stuck in an open-close
      // loop: https://github.com/floating-ui/floating-ui/issues/1910

      if (event.type === 'mouseleave' && isElement(event.relatedTarget) && (_refs$floating$curren = refs.floating.current) != null && _refs$floating$curren.contains(event.relatedTarget)) {
        return;
      } // If any nested child is open, abort.

      if (tree && getChildren(tree.nodesRef.current, nodeId).some(function (_ref2) {
        var context = _ref2.context;
        return context == null ? void 0 : context.open;
      })) {
        return;
      } // The cursor landed, so we destroy the polygon logic

      if ((_refs$floating$curren2 = refs.floating.current) != null && _refs$floating$curren2.contains(targetNode) && !leave) {
        polygonIsDestroyed = true;
        return;
      }
      if (!refs.domReference.current || !refs.floating.current || placement == null || x == null || y == null) {
        return;
      }
      var refRect = refs.domReference.current.getBoundingClientRect();
      var rect = refs.floating.current.getBoundingClientRect();
      var side = placement.split('-')[0];
      var cursorLeaveFromRight = x > rect.right - rect.width / 2;
      var cursorLeaveFromBottom = y > rect.bottom - rect.height / 2; // If the pointer is leaving from the opposite side, the "buffer" logic
      // creates a point where the floating element remains open, but should be
      // ignored.
      // A constant of 1 handles floating point rounding errors.

      if (side === 'top' && y >= refRect.bottom - 1 || side === 'bottom' && y <= refRect.top + 1 || side === 'left' && x >= refRect.right - 1 || side === 'right' && x <= refRect.left + 1) {
        return close();
      } // Ignore when the cursor is within the rectangular trough between the
      // two elements. Since the triangle is created from the cursor point,
      // which can start beyond the ref element's edge, traversing back and
      // forth from the ref to the floating element can cause it to close. This
      // ensures it always remains open in that case.

      switch (side) {
        case 'top':
          if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= refRect.top + 1) {
            return;
          }
          break;
        case 'bottom':
          if (clientX >= rect.left && clientX <= rect.right && clientY >= refRect.bottom - 1 && clientY <= rect.bottom) {
            return;
          }
          break;
        case 'left':
          if (clientX >= rect.left && clientX <= refRect.left + 1 && clientY >= rect.top && clientY <= rect.bottom) {
            return;
          }
          break;
        case 'right':
          if (clientX >= refRect.right - 1 && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
            return;
          }
          break;
      }
      if (polygonIsDestroyed) {
        return close();
      }
      function getPolygon(_ref3) {
        var _ref12 = _slicedToArray(_ref3, 2),
          x = _ref12[0],
          y = _ref12[1];
        var isFloatingWider = rect.width > refRect.width;
        var isFloatingTaller = rect.height > refRect.height;
        switch (side) {
          case 'top':
            {
              var cursorPointOne = [isFloatingWider ? x + buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y + buffer + 1];
              var cursorPointTwo = [isFloatingWider ? x - buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y + buffer + 1];
              var commonPoints = [[rect.left, cursorLeaveFromRight ? rect.bottom - buffer : isFloatingWider ? rect.bottom - buffer : rect.top], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.bottom - buffer : rect.top : rect.bottom - buffer]];
              return [cursorPointOne, cursorPointTwo].concat(commonPoints);
            }
          case 'bottom':
            {
              var _cursorPointOne = [isFloatingWider ? x + buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y - buffer];
              var _cursorPointTwo = [isFloatingWider ? x - buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y - buffer];
              var _commonPoints = [[rect.left, cursorLeaveFromRight ? rect.top + buffer : isFloatingWider ? rect.top + buffer : rect.bottom], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.top + buffer : rect.bottom : rect.top + buffer]];
              return [_cursorPointOne, _cursorPointTwo].concat(_commonPoints);
            }
          case 'left':
            {
              var _cursorPointOne2 = [x + buffer + 1, isFloatingTaller ? y + buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              var _cursorPointTwo2 = [x + buffer + 1, isFloatingTaller ? y - buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              var _commonPoints2 = [[cursorLeaveFromBottom ? rect.right - buffer : isFloatingTaller ? rect.right - buffer : rect.left, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.right - buffer : rect.left : rect.right - buffer, rect.bottom]];
              return [].concat(_commonPoints2, [_cursorPointOne2, _cursorPointTwo2]);
            }
          case 'right':
            {
              var _cursorPointOne3 = [x - buffer, isFloatingTaller ? y + buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              var _cursorPointTwo3 = [x - buffer, isFloatingTaller ? y - buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              var _commonPoints3 = [[cursorLeaveFromBottom ? rect.left + buffer : isFloatingTaller ? rect.left + buffer : rect.right, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.left + buffer : rect.right : rect.left + buffer, rect.bottom]];
              return [_cursorPointOne3, _cursorPointTwo3].concat(_commonPoints3);
            }
        }
      }
      var poly = getPolygon([x, y]);
      if (process.env.NODE_ENV !== "production") {
        debug == null ? void 0 : debug(poly.slice(0, 4).join(', '));
      }
      if (!isPointInPolygon([clientX, clientY], poly)) {
        close();
      } else if (restMs) {
        timeoutId = setTimeout(onClose, restMs);
      }
    };
  };
  fn.__options = {
    blockPointerEvents: blockPointerEvents
  };
  return fn;
}
var DEFAULT_ID = 'floating-ui-root';
var useFloatingPortalNode = function useFloatingPortalNode(_temp) {
  var _ref13 = _temp === void 0 ? {} : _temp,
    _ref13$id = _ref13.id,
    id = _ref13$id === void 0 ? DEFAULT_ID : _ref13$id,
    _ref13$enabled = _ref13.enabled,
    enabled = _ref13$enabled === void 0 ? true : _ref13$enabled;
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    portalEl = _React$useState6[0],
    setPortalEl = _React$useState6[1];
  index(function () {
    if (!enabled) {
      return;
    }
    var rootNode = document.getElementById(id);
    if (rootNode) {
      setPortalEl(rootNode);
    } else {
      var newPortalEl = document.createElement('div');
      newPortalEl.id = id;
      setPortalEl(newPortalEl);
      if (!document.body.contains(newPortalEl)) {
        document.body.appendChild(newPortalEl);
      }
    }
  }, [id, enabled]);
  return portalEl;
};
/**
 * Portals your floating element outside of the main app node.
 * @see https://floating-ui.com/docs/FloatingPortal
 */

var FloatingPortal = function FloatingPortal(_ref) {
  var children = _ref.children,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? DEFAULT_ID : _ref$id,
    _ref$root = _ref.root,
    root = _ref$root === void 0 ? null : _ref$root;
  var portalNode = useFloatingPortalNode({
    id: id,
    enabled: !root
  });
  if (root) {
    return /*#__PURE__*/createPortal(children, root);
  }
  if (portalNode) {
    return /*#__PURE__*/createPortal(children, portalNode);
  }
  return null;
};
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

// Avoid Chrome DevTools blue warning
function getPlatform() {
  var uaData = navigator.userAgentData;
  if (uaData != null && uaData.platform) {
    return uaData.platform;
  }
  return navigator.platform;
}
function getUserAgent() {
  var uaData = navigator.userAgentData;
  if (uaData != null && uaData.brands) {
    return uaData.brands.map(function (_ref) {
      var brand = _ref.brand,
        version = _ref.version;
      return brand + "/" + version;
    }).join(' ');
  }
  return navigator.userAgent;
}
var identifier = 'data-floating-ui-scroll-lock';
/**
 * Provides base styling for a fixed overlay element to dim content or block
 * pointer events behind a floating element.
 * It's a regular `<div>`, so it can be styled via any CSS solution you prefer.
 * @see https://floating-ui.com/docs/FloatingOverlay
 */

var FloatingOverlay = /*#__PURE__*/React.forwardRef(function FloatingOverlay(_ref, ref) {
  var _ref$lockScroll = _ref.lockScroll,
    lockScroll = _ref$lockScroll === void 0 ? false : _ref$lockScroll,
    rest = _objectWithoutProperties(_ref, _excluded);
  index(function () {
    var _window$visualViewpor, _window$visualViewpor2, _window$visualViewpor3, _window$visualViewpor4;
    if (!lockScroll) {
      return;
    }
    var alreadyLocked = document.body.hasAttribute(identifier);
    if (alreadyLocked) {
      return;
    }
    document.body.setAttribute(identifier, ''); // RTL <body> scrollbar

    var scrollbarX = Math.round(document.documentElement.getBoundingClientRect().left) + document.documentElement.scrollLeft;
    var paddingProp = scrollbarX ? 'paddingLeft' : 'paddingRight';
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth; // Only iOS doesn't respect `overflow: hidden` on document.body, and this
    // technique has fewer side effects.

    if (!/iP(hone|ad|od)|iOS/.test(getPlatform())) {
      Object.assign(document.body.style, _defineProperty({
        overflow: 'hidden'
      }, paddingProp, scrollbarWidth + "px"));
      return function () {
        document.body.removeAttribute(identifier);
        Object.assign(document.body.style, _defineProperty({
          overflow: ''
        }, paddingProp, ''));
      };
    } // iOS 12 does not support `visuaViewport`.

    var offsetLeft = (_window$visualViewpor = (_window$visualViewpor2 = window.visualViewport) == null ? void 0 : _window$visualViewpor2.offsetLeft) != null ? _window$visualViewpor : 0;
    var offsetTop = (_window$visualViewpor3 = (_window$visualViewpor4 = window.visualViewport) == null ? void 0 : _window$visualViewpor4.offsetTop) != null ? _window$visualViewpor3 : 0;
    var scrollX = window.pageXOffset;
    var scrollY = window.pageYOffset;
    Object.assign(document.body.style, _defineProperty({
      position: 'fixed',
      overflow: 'hidden',
      top: -(scrollY - Math.floor(offsetTop)) + "px",
      left: -(scrollX - Math.floor(offsetLeft)) + "px",
      right: '0'
    }, paddingProp, scrollbarWidth + "px"));
    return function () {
      Object.assign(document.body.style, _defineProperty({
        position: '',
        overflow: '',
        top: '',
        left: '',
        right: ''
      }, paddingProp, ''));
      document.body.removeAttribute(identifier);
      window.scrollTo(scrollX, scrollY);
    };
  }, [lockScroll]);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, rest, {
    style: _objectSpread({
      position: 'fixed',
      overflow: 'auto',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }, rest.style)
  }));
});

/**
 * Find the real active element. Traverses into shadowRoots.
 */
function activeElement(doc) {
  var activeElement = doc.activeElement;
  while (((_activeElement = activeElement) == null ? void 0 : (_activeElement$shadow = _activeElement.shadowRoot) == null ? void 0 : _activeElement$shadow.activeElement) != null) {
    var _activeElement, _activeElement$shadow;
    activeElement = activeElement.shadowRoot.activeElement;
  }
  return activeElement;
}
function getAncestors(nodes, id) {
  var _nodes$find;
  var allAncestors = [];
  var currentParentId = (_nodes$find = nodes.find(function (node) {
    return node.id === id;
  })) == null ? void 0 : _nodes$find.parentId;
  while (currentParentId) {
    var currentNode = nodes.find(function (node) {
      return node.id === currentParentId;
    });
    currentParentId = currentNode == null ? void 0 : currentNode.parentId;
    if (currentNode) {
      allAncestors = allAncestors.concat(currentNode);
    }
  }
  return allAncestors;
}
function getTarget(event) {
  if ('composedPath' in event) {
    return event.composedPath()[0];
  } // TS thinks `event` is of type never as it assumes all browsers support
  // `composedPath()`, but browsers without shadow DOM don't.

  return event.target;
}
var TYPEABLE_SELECTOR = "input:not([type='hidden']):not([disabled])," + "[contenteditable]:not([contenteditable='false']),textarea:not([disabled])";
function isTypeableElement(element) {
  return isHTMLElement(element) && element.matches(TYPEABLE_SELECTOR);
}
function stopEvent(event) {
  event.preventDefault();
  event.stopPropagation();
}
function useLatestRef(value) {
  var ref = useRef(value);
  index(function () {
    ref.current = value;
  });
  return ref;
}
function focus(el, preventScroll) {
  if (preventScroll === void 0) {
    preventScroll = false;
  }

  // `mousedown` clicks occur before `focus`, so the button will steal the
  // focus unless we wait a frame.
  requestAnimationFrame(function () {
    el == null ? void 0 : el.focus({
      preventScroll: preventScroll
    });
  });
}
var SELECTOR = 'select:not([disabled]),a[href],button:not([disabled]),[tabindex],' + 'iframe,object,embed,area[href],audio[controls],video[controls],' + TYPEABLE_SELECTOR;
var FocusGuard = /*#__PURE__*/React.forwardRef(function FocusGuard(props, ref) {
  return /*#__PURE__*/React.createElement("span", _extends({}, props, {
    ref: ref,
    tabIndex: 0,
    style: {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      outline: '0'
    }
  }));
});

/**
 * Provides focus management for the floating element.
 * @see https://floating-ui.com/docs/FloatingFocusManager
 */
function FloatingFocusManager(_ref) {
  var _ref$context = _ref.context,
    refs = _ref$context.refs,
    nodeId = _ref$context.nodeId,
    onOpenChange = _ref$context.onOpenChange,
    dataRef = _ref$context.dataRef,
    events = _ref$context.events,
    children = _ref.children,
    _ref$order = _ref.order,
    order = _ref$order === void 0 ? ['content'] : _ref$order,
    _ref$endGuard = _ref.endGuard,
    endGuard = _ref$endGuard === void 0 ? true : _ref$endGuard,
    _ref$initialFocus = _ref.initialFocus,
    initialFocus = _ref$initialFocus === void 0 ? 0 : _ref$initialFocus,
    _ref$returnFocus = _ref.returnFocus,
    returnFocus = _ref$returnFocus === void 0 ? true : _ref$returnFocus,
    _ref$modal = _ref.modal,
    modal = _ref$modal === void 0 ? true : _ref$modal;
  var orderRef = useLatestRef(order);
  var tree = useFloatingTree();
  var getTabbableElements = React.useCallback(function () {
    return orderRef.current.map(function (type) {
      if (type === 'reference') {
        return refs.domReference.current;
      }
      if (refs.floating.current && type === 'floating') {
        return refs.floating.current;
      }
      if (type === 'content') {
        var _refs$floating$curren, _refs$floating$curren2;
        return Array.from((_refs$floating$curren = (_refs$floating$curren2 = refs.floating.current) == null ? void 0 : _refs$floating$curren2.querySelectorAll(SELECTOR)) != null ? _refs$floating$curren : []);
      }
      return null;
    }).flat().filter(function (el) {
      if (el === refs.floating.current || el === refs.domReference.current) {
        return true;
      }
      if (isHTMLElement(el)) {
        var _el$getAttribute;
        var tabIndex = (_el$getAttribute = el.getAttribute('tabindex')) != null ? _el$getAttribute : '0';
        return tabIndex[0].trim() !== '-';
      }
    });
  }, [orderRef, refs]);
  React.useEffect(function () {
    if (!modal) {
      return;
    } // If the floating element has no focusable elements inside it, fallback
    // to focusing the floating element and preventing tab navigation

    var noTabbableContentElements = getTabbableElements().filter(function (el) {
      return el !== refs.floating.current && el !== refs.domReference.current;
    }).length === 0;
    function onKeyDown(event) {
      if (event.key === 'Tab') {
        if (noTabbableContentElements) {
          stopEvent(event);
        }
        var els = getTabbableElements();
        var target = getTarget(event);
        if (orderRef.current[0] === 'reference' && target === refs.domReference.current) {
          stopEvent(event);
          if (event.shiftKey) {
            focus(els[els.length - 1]);
          } else {
            focus(els[1]);
          }
        }
        if (orderRef.current[1] === 'floating' && target === refs.floating.current && event.shiftKey) {
          stopEvent(event);
          focus(els[0]);
        }
      }
    }
    var doc = getDocument(refs.floating.current);
    doc.addEventListener('keydown', onKeyDown);
    return function () {
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [modal, getTabbableElements, orderRef, refs]);
  React.useEffect(function () {
    var isPointerDown = false;
    function onFocusOut(event) {
      var _refs$floating$curren3, _getAncestors;
      var relatedTarget = event.relatedTarget;
      var focusMovedOutsideFloating = !((_refs$floating$curren3 = refs.floating.current) != null && _refs$floating$curren3.contains(relatedTarget));
      var focusMovedOutsideReference = isElement(refs.domReference.current) && !refs.domReference.current.contains(relatedTarget);
      var isChildOpen = tree && getChildren(tree.nodesRef.current, nodeId).length > 0;
      var isParentRelated = tree && event.currentTarget === refs.domReference.current && ((_getAncestors = getAncestors(tree.nodesRef.current, nodeId)) == null ? void 0 : _getAncestors.some(function (node) {
        var _node$context, _node$context$refs$fl;
        return (_node$context = node.context) == null ? void 0 : (_node$context$refs$fl = _node$context.refs.floating.current) == null ? void 0 : _node$context$refs$fl.contains(relatedTarget);
      }));
      if (focusMovedOutsideFloating && focusMovedOutsideReference && !isChildOpen && !isParentRelated && !isPointerDown) {
        onOpenChange(false);
      }
    }
    function onPointerDown() {
      // In Safari, buttons *lose* focus when pressing them. This causes the
      // reference `focusout` to fire, which closes the floating element.
      isPointerDown = true;
      setTimeout(function () {
        isPointerDown = false;
      });
    }
    var floating = refs.floating.current;
    var reference = refs.domReference.current;
    if (floating && isHTMLElement(reference)) {
      if (!modal) {
        floating.addEventListener('focusout', onFocusOut);
        reference.addEventListener('focusout', onFocusOut);
        reference.addEventListener('pointerdown', onPointerDown);
      }
      var cleanup;
      if (modal) {
        if (orderRef.current.includes('reference')) {
          cleanup = hideOthers([reference, floating]);
        } else {
          cleanup = hideOthers(floating);
        }
      }
      return function () {
        if (!modal) {
          floating.removeEventListener('focusout', onFocusOut);
          reference.removeEventListener('focusout', onFocusOut);
          reference.removeEventListener('pointerdown', onPointerDown);
        }
        cleanup == null ? void 0 : cleanup();
      };
    }
  }, [nodeId, tree, modal, onOpenChange, orderRef, dataRef, getTabbableElements, refs]);
  React.useEffect(function () {
    var floating = refs.floating.current;
    var doc = getDocument(floating);
    var returnFocusValue = returnFocus;
    var preventReturnFocusScroll = false;
    var previouslyFocusedElement = activeElement(doc);
    if (previouslyFocusedElement === doc.body && refs.domReference.current) {
      previouslyFocusedElement = refs.domReference.current;
    }
    if (typeof initialFocus === 'number') {
      var _getTabbableElements$;
      var el = (_getTabbableElements$ = getTabbableElements()[initialFocus]) != null ? _getTabbableElements$ : floating;
      focus(el, el === floating);
    } else if (isHTMLElement(initialFocus.current)) {
      var _initialFocus$current;
      var _el = (_initialFocus$current = initialFocus.current) != null ? _initialFocus$current : floating;
      focus(_el, _el === floating);
    } // Dismissing via outside press should always ignore `returnFocus` to
    // prevent unwanted scrolling.

    function onDismiss(allowReturnFocus) {
      if (allowReturnFocus === void 0) {
        allowReturnFocus = false;
      }
      if (typeof allowReturnFocus === 'object') {
        returnFocusValue = true;
        preventReturnFocusScroll = allowReturnFocus.preventScroll;
      } else {
        returnFocusValue = allowReturnFocus;
      }
    }
    events.on('dismiss', onDismiss);
    return function () {
      events.off('dismiss', onDismiss);
      if (returnFocusValue && isHTMLElement(previouslyFocusedElement)) {
        focus(previouslyFocusedElement, preventReturnFocusScroll);
      }
    };
  }, [getTabbableElements, initialFocus, returnFocus, refs, events]);
  var isTypeableCombobox = function isTypeableCombobox() {
    var _refs$domReference$cu;
    return ((_refs$domReference$cu = refs.domReference.current) == null ? void 0 : _refs$domReference$cu.getAttribute('role')) === 'combobox' && isTypeableElement(refs.domReference.current);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, modal && /*#__PURE__*/React.createElement(FocusGuard, {
    onFocus: function onFocus(event) {
      if (isTypeableCombobox()) {
        return;
      }
      stopEvent(event);
      var els = getTabbableElements();
      if (order[0] === 'reference') {
        focus(els[0]);
      } else {
        focus(els[els.length - 1]);
      }
    }
  }), /*#__PURE__*/React.cloneElement(children, order.includes('floating') ? {
    tabIndex: 0
  } : {}), modal && endGuard && /*#__PURE__*/React.createElement(FocusGuard, {
    onFocus: function onFocus(event) {
      if (isTypeableCombobox()) {
        return;
      }
      stopEvent(event);
      focus(getTabbableElements()[0]);
    }
  }));
}
function usePrevious(value) {
  var ref = useRef();
  index(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
}
function getDelay(value, prop, pointerType) {
  if (pointerType && pointerType !== 'mouse') {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  return value == null ? void 0 : value[prop];
}

/**
 * Adds hover event listeners that change the open state, like CSS :hover.
 * @see https://floating-ui.com/docs/useHover
 */
var useHover = function useHover(context, _temp) {
  var _ref14 = _temp === void 0 ? {} : _temp,
    _ref14$enabled = _ref14.enabled,
    enabled = _ref14$enabled === void 0 ? true : _ref14$enabled,
    _ref14$delay = _ref14.delay,
    delay = _ref14$delay === void 0 ? 0 : _ref14$delay,
    _ref14$handleClose = _ref14.handleClose,
    handleClose = _ref14$handleClose === void 0 ? null : _ref14$handleClose,
    _ref14$mouseOnly = _ref14.mouseOnly,
    mouseOnly = _ref14$mouseOnly === void 0 ? false : _ref14$mouseOnly,
    _ref14$restMs = _ref14.restMs,
    restMs = _ref14$restMs === void 0 ? 0 : _ref14$restMs,
    _ref14$move = _ref14.move,
    move = _ref14$move === void 0 ? true : _ref14$move;
  var open = context.open,
    onOpenChange = context.onOpenChange,
    dataRef = context.dataRef,
    events = context.events,
    refs = context.refs,
    _ = context._;
  var tree = useFloatingTree();
  var parentId = useFloatingParentNodeId();
  var handleCloseRef = useLatestRef(handleClose);
  var delayRef = useLatestRef(delay);
  var previousOpen = usePrevious(open);
  var pointerTypeRef = React.useRef();
  var timeoutRef = React.useRef();
  var handlerRef = React.useRef();
  var restTimeoutRef = React.useRef();
  var blockMouseMoveRef = React.useRef(true);
  var performedPointerEventsMutationRef = React.useRef(false);
  var isHoverOpen = React.useCallback(function () {
    var _dataRef$current$open;
    var type = (_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type;
    return (type == null ? void 0 : type.includes('mouse')) && type !== 'mousedown';
  }, [dataRef]);
  React.useEffect(function () {
    if (!enabled) {
      return;
    }
    function onDismiss() {
      clearTimeout(timeoutRef.current);
      clearTimeout(restTimeoutRef.current);
      blockMouseMoveRef.current = true;
    }
    events.on('dismiss', onDismiss);
    return function () {
      events.off('dismiss', onDismiss);
    };
  }, [enabled, events, refs]);
  React.useEffect(function () {
    if (!enabled || !handleCloseRef.current) {
      return;
    }
    function onLeave() {
      if (isHoverOpen()) {
        onOpenChange(false);
      }
    }
    var html = getDocument(refs.floating.current).documentElement;
    html.addEventListener('mouseleave', onLeave);
    return function () {
      html.removeEventListener('mouseleave', onLeave);
    };
  }, [refs, onOpenChange, enabled, handleCloseRef, dataRef, isHoverOpen]);
  var closeWithDelay = React.useCallback(function (runElseBranch) {
    if (runElseBranch === void 0) {
      runElseBranch = true;
    }
    var closeDelay = getDelay(delayRef.current, 'close', pointerTypeRef.current);
    if (closeDelay && !handlerRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(function () {
        return onOpenChange(false);
      }, closeDelay);
    } else if (runElseBranch) {
      clearTimeout(timeoutRef.current);
      onOpenChange(false);
    }
  }, [delayRef, onOpenChange]);
  var cleanupPointerMoveHandler = React.useCallback(function () {
    if (handlerRef.current) {
      getDocument(refs.floating.current).removeEventListener('pointermove', handlerRef.current);
      handlerRef.current = undefined;
    }
  }, [refs]);
  var clearPointerEvents = React.useCallback(function () {
    getDocument(refs.floating.current).body.style.pointerEvents = '';
    performedPointerEventsMutationRef.current = false;
  }, [refs]); // Registering the mouse events on the reference directly to bypass React's
  // delegation system. If the cursor was on a disabled element and then entered
  // the reference (no gap), `mouseenter` doesn't fire in the delegation system.

  React.useEffect(function () {
    if (!enabled) {
      return;
    }
    function isClickLikeOpenEvent() {
      return dataRef.current.openEvent ? ['click', 'mousedown'].includes(dataRef.current.openEvent.type) : false;
    }
    function onMouseEnter(event) {
      clearTimeout(timeoutRef.current);
      blockMouseMoveRef.current = false;
      if (mouseOnly && pointerTypeRef.current !== 'mouse' || restMs > 0 && getDelay(delayRef.current, 'open') === 0) {
        return;
      }
      dataRef.current.openEvent = event;
      var openDelay = getDelay(delayRef.current, 'open', pointerTypeRef.current);
      if (openDelay) {
        timeoutRef.current = setTimeout(function () {
          onOpenChange(true);
        }, openDelay);
      } else {
        onOpenChange(true);
      }
    }
    function onMouseLeave(event) {
      if (isClickLikeOpenEvent()) {
        return;
      }
      var doc = getDocument(refs.floating.current);
      clearTimeout(restTimeoutRef.current);
      if (handleCloseRef.current) {
        clearTimeout(timeoutRef.current);
        handlerRef.current && doc.removeEventListener('pointermove', handlerRef.current);
        handlerRef.current = handleCloseRef.current(_objectSpread(_objectSpread({}, context), {}, {
          tree: tree,
          x: event.clientX,
          y: event.clientY,
          onClose: function onClose() {
            clearPointerEvents();
            cleanupPointerMoveHandler();
            closeWithDelay();
          }
        }));
        doc.addEventListener('pointermove', handlerRef.current);
        return;
      }
      closeWithDelay();
    } // Ensure the floating element closes after scrolling even if the pointer
    // did not move.
    // https://github.com/floating-ui/floating-ui/discussions/1692

    function onScrollMouseLeave(event) {
      if (isClickLikeOpenEvent()) {
        return;
      }
      handleCloseRef.current == null ? void 0 : handleCloseRef.current(_objectSpread(_objectSpread({}, context), {}, {
        tree: tree,
        x: event.clientX,
        y: event.clientY,
        leave: true,
        onClose: function onClose() {
          clearPointerEvents();
          cleanupPointerMoveHandler();
          closeWithDelay();
        }
      }))(event);
    }
    var floating = refs.floating.current;
    var reference = refs.domReference.current;
    if (isElement(reference)) {
      open && reference.addEventListener('mouseleave', onScrollMouseLeave);
      floating == null ? void 0 : floating.addEventListener('mouseleave', onScrollMouseLeave);
      move && reference.addEventListener('mousemove', onMouseEnter, {
        once: true
      });
      reference.addEventListener('mouseenter', onMouseEnter);
      reference.addEventListener('mouseleave', onMouseLeave);
      return function () {
        open && reference.removeEventListener('mouseleave', onScrollMouseLeave);
        floating == null ? void 0 : floating.removeEventListener('mouseleave', onScrollMouseLeave);
        move && reference.removeEventListener('mousemove', onMouseEnter);
        reference.removeEventListener('mouseenter', onMouseEnter);
        reference.removeEventListener('mouseleave', onMouseLeave);
      };
    }
  }, [
  // Ensure the effect is re-run when the reference changes.
  // https://github.com/floating-ui/floating-ui/issues/1833
  _.domReference, enabled, context, mouseOnly, restMs, move, closeWithDelay, cleanupPointerMoveHandler, clearPointerEvents, onOpenChange, open, tree, refs, delayRef, handleCloseRef, dataRef]); // Block pointer-events of every element other than the reference and floating
  // while the floating element is open and has a `handleClose` handler. Also
  // handles nested floating elements.
  // https://github.com/floating-ui/floating-ui/issues/1722

  index(function () {
    if (!enabled) {
      return;
    }
    if (open && handleCloseRef.current && handleCloseRef.current.__options.blockPointerEvents && isHoverOpen()) {
      getDocument(refs.floating.current).body.style.pointerEvents = 'none';
      performedPointerEventsMutationRef.current = true;
      var reference = refs.domReference.current;
      var floating = refs.floating.current;
      if (isElement(reference) && floating) {
        var _tree$nodesRef$curren, _tree$nodesRef$curren2;
        var parentFloating = tree == null ? void 0 : (_tree$nodesRef$curren = tree.nodesRef.current.find(function (node) {
          return node.id === parentId;
        })) == null ? void 0 : (_tree$nodesRef$curren2 = _tree$nodesRef$curren.context) == null ? void 0 : _tree$nodesRef$curren2.refs.floating.current;
        if (parentFloating) {
          parentFloating.style.pointerEvents = '';
        }
        reference.style.pointerEvents = 'auto';
        floating.style.pointerEvents = 'auto';
        return function () {
          reference.style.pointerEvents = '';
          floating.style.pointerEvents = '';
        };
      }
    }
  }, [enabled, open, parentId, refs, tree, handleCloseRef, dataRef, isHoverOpen]);
  index(function () {
    if (previousOpen && !open) {
      pointerTypeRef.current = undefined;
      cleanupPointerMoveHandler();
      clearPointerEvents();
    }
  });
  React.useEffect(function () {
    return function () {
      cleanupPointerMoveHandler();
      clearTimeout(timeoutRef.current);
      clearTimeout(restTimeoutRef.current);
      if (performedPointerEventsMutationRef.current) {
        clearPointerEvents();
      }
    };
  }, [enabled, cleanupPointerMoveHandler, clearPointerEvents]);
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    function setPointerRef(event) {
      pointerTypeRef.current = event.pointerType;
    }
    return {
      reference: {
        onPointerDown: setPointerRef,
        onPointerEnter: setPointerRef,
        onMouseMove: function onMouseMove() {
          if (open || restMs === 0) {
            return;
          }
          clearTimeout(restTimeoutRef.current);
          restTimeoutRef.current = setTimeout(function () {
            if (!blockMouseMoveRef.current) {
              onOpenChange(true);
            }
          }, restMs);
        }
      },
      floating: {
        onMouseEnter: function onMouseEnter() {
          clearTimeout(timeoutRef.current);
        },
        onMouseLeave: function onMouseLeave() {
          closeWithDelay(false);
        }
      }
    };
  }, [enabled, restMs, open, onOpenChange, closeWithDelay]);
};
var FloatingDelayGroupContext = /*#__PURE__*/React.createContext({
  delay: 1000,
  initialDelay: 1000,
  currentId: null,
  setCurrentId: function setCurrentId() {},
  setState: function setState() {}
});
var useDelayGroupContext = function useDelayGroupContext() {
  return React.useContext(FloatingDelayGroupContext);
};
/**
 * Provides context for a group of floating elements that should share a
 * `delay`.
 * @see https://floating-ui.com/docs/FloatingDelayGroup
 */

var FloatingDelayGroup = function FloatingDelayGroup(_ref) {
  var children = _ref.children,
    delay = _ref.delay;
  var _React$useState7 = React.useState({
      delay: delay,
      initialDelay: delay,
      currentId: null
    }),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    state = _React$useState8[0],
    setState = _React$useState8[1];
  var setCurrentId = React.useCallback(function (currentId) {
    setState(function (state) {
      return _objectSpread(_objectSpread({}, state), {}, {
        currentId: currentId
      });
    });
  }, []);
  return /*#__PURE__*/React.createElement(FloatingDelayGroupContext.Provider, {
    value: React.useMemo(function () {
      return _objectSpread(_objectSpread({}, state), {}, {
        setState: setState,
        setCurrentId: setCurrentId
      });
    }, [state, setState, setCurrentId])
  }, children);
};
var useDelayGroup = function useDelayGroup(_ref2, _ref3) {
  var open = _ref2.open,
    onOpenChange = _ref2.onOpenChange;
  var id = _ref3.id;
  var _useDelayGroupContext = useDelayGroupContext(),
    currentId = _useDelayGroupContext.currentId,
    initialDelay = _useDelayGroupContext.initialDelay,
    setState = _useDelayGroupContext.setState;
  React.useEffect(function () {
    if (currentId) {
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          delay: {
            open: 1,
            close: getDelay(initialDelay, 'close')
          }
        });
      });
      if (currentId !== id) {
        onOpenChange(false);
      }
    }
  }, [id, onOpenChange, setState, currentId, initialDelay]);
  React.useEffect(function () {
    if (!open && currentId === id) {
      onOpenChange(false);
      setState(function (state) {
        return _objectSpread(_objectSpread({}, state), {}, {
          delay: initialDelay,
          currentId: null
        });
      });
    }
  }, [open, setState, currentId, id, onOpenChange, initialDelay]);
};
function getArgsWithCustomFloatingHeight(args, prop) {
  return _objectSpread(_objectSpread({}, args), {}, {
    rects: _objectSpread(_objectSpread({}, args.rects), {}, {
      floating: _objectSpread(_objectSpread({}, args.rects.floating), {}, {
        height: args.elements.floating[prop]
      })
    })
  });
}
var inner = function inner(options) {
  return {
    name: 'inner',
    options: options,
    fn: function fn(middlewareArguments) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var listRef, overflowRef, onFallbackChange, _options$offset, innerOffset, _options$index, index, _options$minItemsVisi, minItemsVisible, _options$referenceOve, referenceOverflowThreshold, detectOverflowOptions, rects, floating, item, nextArgs, overflow, refOverflow, diffY, nextY, maxHeight;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              listRef = options.listRef, overflowRef = options.overflowRef, onFallbackChange = options.onFallbackChange, _options$offset = options.offset, innerOffset = _options$offset === void 0 ? 0 : _options$offset, _options$index = options.index, index = _options$index === void 0 ? 0 : _options$index, _options$minItemsVisi = options.minItemsVisible, minItemsVisible = _options$minItemsVisi === void 0 ? 4 : _options$minItemsVisi, _options$referenceOve = options.referenceOverflowThreshold, referenceOverflowThreshold = _options$referenceOve === void 0 ? 0 : _options$referenceOve, detectOverflowOptions = _objectWithoutProperties(options, _excluded2);
              rects = middlewareArguments.rects, floating = middlewareArguments.elements.floating;
              item = listRef.current[index];
              if (process.env.NODE_ENV !== "production") {
                if (!middlewareArguments.placement.startsWith('bottom')) {
                  console.warn(['Floating UI: `placement` side must be "bottom" when using the', '`inner` middleware.'].join(' '));
                }
              }
              if (item) {
                _context.next = 6;
                break;
              }
              return _context.abrupt("return", {});
            case 6:
              _context.t0 = _objectSpread;
              _context.t1 = _objectSpread({}, middlewareArguments);
              _context.next = 10;
              return offset(-item.offsetTop - rects.reference.height / 2 - item.offsetHeight / 2 - innerOffset).fn(middlewareArguments);
            case 10:
              _context.t2 = _context.sent;
              nextArgs = (0, _context.t0)(_context.t1, _context.t2);
              _context.next = 14;
              return detectOverflow(getArgsWithCustomFloatingHeight(nextArgs, 'scrollHeight'), detectOverflowOptions);
            case 14:
              overflow = _context.sent;
              _context.next = 17;
              return detectOverflow(nextArgs, _objectSpread(_objectSpread({}, detectOverflowOptions), {}, {
                elementContext: 'reference'
              }));
            case 17:
              refOverflow = _context.sent;
              diffY = Math.max(0, overflow.top);
              nextY = nextArgs.y + diffY;
              maxHeight = Math.max(0, floating.scrollHeight - diffY - Math.max(0, overflow.bottom));
              floating.style.maxHeight = maxHeight + "px";
              floating.scrollTop = diffY; // There is not enough space, fallback to standard anchored positioning

              if (onFallbackChange) {
                if (floating.offsetHeight < item.offsetHeight * Math.min(minItemsVisible, listRef.current.length - 1) - 1 || refOverflow.top >= -referenceOverflowThreshold || refOverflow.bottom >= -referenceOverflowThreshold) {
                  flushSync(function () {
                    return onFallbackChange(true);
                  });
                } else {
                  flushSync(function () {
                    return onFallbackChange(false);
                  });
                }
              }
              if (!overflowRef) {
                _context.next = 28;
                break;
              }
              _context.next = 27;
              return detectOverflow(getArgsWithCustomFloatingHeight(_objectSpread(_objectSpread({}, nextArgs), {}, {
                y: nextY
              }), 'offsetHeight'), detectOverflowOptions);
            case 27:
              overflowRef.current = _context.sent;
            case 28:
              return _context.abrupt("return", {
                y: nextY
              });
            case 29:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }))();
    }
  };
};
var useInnerOffset = function useInnerOffset(_ref, _ref2) {
  var open = _ref.open,
    refs = _ref.refs;
  var _ref2$enabled = _ref2.enabled,
    enabled = _ref2$enabled === void 0 ? true : _ref2$enabled,
    overflowRef = _ref2.overflowRef,
    unstable_onChange = _ref2.onChange;
  var onChange = useEvent(unstable_onChange);
  var controlledScrollingRef = React.useRef(false);
  var prevScrollTopRef = React.useRef(null);
  var initialOverflowRef = React.useRef(null);
  React.useEffect(function () {
    if (!enabled) {
      return;
    }
    function onWheel(e) {
      if (e.ctrlKey || !el || overflowRef.current == null) {
        return;
      }
      var dY = e.deltaY;
      var isAtTop = overflowRef.current.top >= -0.5;
      var isAtBottom = overflowRef.current.bottom >= -0.5;
      var remainingScroll = el.scrollHeight - el.clientHeight;
      var sign = dY < 0 ? -1 : 1;
      var method = dY < 0 ? 'max' : 'min';
      if (el.scrollHeight <= el.clientHeight) {
        return;
      }
      if (!isAtTop && dY > 0 || !isAtBottom && dY < 0) {
        e.preventDefault();
        flushSync(function () {
          onChange(function (d) {
            return d + Math[method](dY, remainingScroll * sign);
          });
        });
      } else if (/firefox/i.test(getUserAgent())) {
        // Needed to propagate scrolling during momentum scrolling phase once
        // it gets limited by the boundary. UX improvement, not critical.
        el.scrollTop += dY;
      }
    }
    var el = refs.floating.current;
    if (open && el) {
      el.addEventListener('wheel', onWheel); // Wait for the position to be ready.

      requestAnimationFrame(function () {
        prevScrollTopRef.current = el.scrollTop;
        if (overflowRef.current != null) {
          initialOverflowRef.current = _objectSpread({}, overflowRef.current);
        }
      });
      return function () {
        prevScrollTopRef.current = null;
        initialOverflowRef.current = null;
        el.removeEventListener('wheel', onWheel);
      };
    }
  }, [enabled, open, refs, overflowRef, onChange]);
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    return {
      floating: {
        onKeyDown: function onKeyDown() {
          controlledScrollingRef.current = true;
        },
        onWheel: function onWheel() {
          controlledScrollingRef.current = false;
        },
        onPointerMove: function onPointerMove() {
          controlledScrollingRef.current = false;
        },
        onScroll: function onScroll() {
          var el = refs.floating.current;
          if (!overflowRef.current || !el || !controlledScrollingRef.current) {
            return;
          }
          if (prevScrollTopRef.current !== null) {
            var scrollDiff = el.scrollTop - prevScrollTopRef.current;
            if (overflowRef.current.bottom < -0.5 && scrollDiff < -1 || overflowRef.current.top < -0.5 && scrollDiff > 1) {
              flushSync(function () {
                return onChange(function (d) {
                  return d + scrollDiff;
                });
              });
            }
          } // [Firefox] Wait for the height change to have been applied.

          requestAnimationFrame(function () {
            prevScrollTopRef.current = el.scrollTop;
          });
        }
      }
    };
  }, [enabled, overflowRef, refs, onChange]);
};

/**
 * Adds relevant screen reader props for a given element `role`.
 * @see https://floating-ui.com/docs/useRole
 */
var useRole = function useRole(_ref, _temp) {
  var open = _ref.open;
  var _ref15 = _temp === void 0 ? {} : _temp,
    _ref15$enabled = _ref15.enabled,
    enabled = _ref15$enabled === void 0 ? true : _ref15$enabled,
    _ref15$role = _ref15.role,
    role = _ref15$role === void 0 ? 'dialog' : _ref15$role;
  var rootId = useId();
  var referenceId = useId();
  return React.useMemo(function () {
    var floatingProps = {
      id: rootId,
      role: role
    };
    if (!enabled) {
      return {};
    }
    if (role === 'tooltip') {
      return {
        reference: {
          'aria-describedby': open ? rootId : undefined
        },
        floating: floatingProps
      };
    }
    return {
      reference: _objectSpread(_objectSpread({
        'aria-expanded': open ? 'true' : 'false',
        'aria-haspopup': role === 'alertdialog' ? 'dialog' : role,
        'aria-controls': open ? rootId : undefined
      }, role === 'listbox' && {
        role: 'combobox'
      }), role === 'menu' && {
        id: referenceId
      }),
      floating: _objectSpread(_objectSpread({}, floatingProps), role === 'menu' && {
        'aria-labelledby': referenceId
      })
    };
  }, [enabled, role, open, rootId, referenceId]);
};
function isButtonTarget(event) {
  return isHTMLElement(event.target) && event.target.tagName === 'BUTTON';
}
function isSpaceIgnored(element) {
  return isTypeableElement(element);
}

/**
 * Adds click event listeners that change the open state.
 * @see https://floating-ui.com/docs/useClick
 */
var useClick = function useClick(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    dataRef = _ref.dataRef,
    refs = _ref.refs;
  var _ref16 = _temp === void 0 ? {} : _temp,
    _ref16$enabled = _ref16.enabled,
    enabled = _ref16$enabled === void 0 ? true : _ref16$enabled,
    _ref16$event = _ref16.event,
    eventOption = _ref16$event === void 0 ? 'click' : _ref16$event,
    _ref16$toggle = _ref16.toggle,
    toggle = _ref16$toggle === void 0 ? true : _ref16$toggle,
    _ref16$ignoreMouse = _ref16.ignoreMouse,
    ignoreMouse = _ref16$ignoreMouse === void 0 ? false : _ref16$ignoreMouse,
    _ref16$keyboardHandle = _ref16.keyboardHandlers,
    keyboardHandlers = _ref16$keyboardHandle === void 0 ? true : _ref16$keyboardHandle;
  var pointerTypeRef = React.useRef();
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    return {
      reference: {
        onPointerDown: function onPointerDown(event) {
          pointerTypeRef.current = event.pointerType;
        },
        onMouseDown: function onMouseDown(event) {
          // Ignore all buttons except for the "main" button.
          // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
          if (event.button !== 0) {
            return;
          }
          if (pointerTypeRef.current === 'mouse' && ignoreMouse) {
            return;
          }
          if (eventOption === 'click') {
            return;
          }
          if (open) {
            if (toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'mousedown' : true)) {
              onOpenChange(false);
            }
          } else {
            onOpenChange(true);
          }
          dataRef.current.openEvent = event.nativeEvent;
        },
        onClick: function onClick(event) {
          if (eventOption === 'mousedown' && pointerTypeRef.current) {
            pointerTypeRef.current = undefined;
            return;
          }
          if (pointerTypeRef.current === 'mouse' && ignoreMouse) {
            return;
          }
          if (open) {
            if (toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'click' : true)) {
              onOpenChange(false);
            }
          } else {
            onOpenChange(true);
          }
          dataRef.current.openEvent = event.nativeEvent;
        },
        onKeyDown: function onKeyDown(event) {
          pointerTypeRef.current = undefined;
          if (!keyboardHandlers) {
            return;
          }
          if (isButtonTarget(event)) {
            return;
          }
          if (event.key === ' ' && !isSpaceIgnored(refs.domReference.current)) {
            // Prvent scrolling
            event.preventDefault();
          }
          if (event.key === 'Enter') {
            if (open) {
              if (toggle) {
                onOpenChange(false);
              }
            } else {
              onOpenChange(true);
            }
          }
        },
        onKeyUp: function onKeyUp(event) {
          if (!keyboardHandlers) {
            return;
          }
          if (isButtonTarget(event) || isSpaceIgnored(refs.domReference.current)) {
            return;
          }
          if (event.key === ' ') {
            if (open) {
              if (toggle) {
                onOpenChange(false);
              }
            } else {
              onOpenChange(true);
            }
          }
        }
      }
    };
  }, [enabled, dataRef, eventOption, ignoreMouse, keyboardHandlers, refs, toggle, open, onOpenChange]);
};

/**
 * Check whether the event.target is within the provided node. Uses event.composedPath if available for custom element support.
 *
 * @param event The event whose target/composedPath to check
 * @param node The node to check against
 * @returns Whether the event.target/composedPath is within the node.
 */
function isEventTargetWithin(event, node) {
  if (node == null) {
    return false;
  }
  if ('composedPath' in event) {
    return event.composedPath().includes(node);
  } // TS thinks `event` is of type never as it assumes all browsers support composedPath, but browsers without shadow dom don't

  var e = event;
  return e.target != null && node.contains(e.target);
}
var bubbleHandlerKeys = {
  pointerdown: 'onPointerDown',
  mousedown: 'onMouseDown',
  click: 'onClick'
};
var captureHandlerKeys = {
  pointerdown: 'onPointerDownCapture',
  mousedown: 'onMouseDownCapture',
  click: 'onClickCapture'
};

/**
 * Adds listeners that dismiss (close) the floating element.
 * @see https://floating-ui.com/docs/useDismiss
 */
var useDismiss = function useDismiss(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    refs = _ref.refs,
    events = _ref.events,
    nodeId = _ref.nodeId;
  var _ref17 = _temp === void 0 ? {} : _temp,
    _ref17$enabled = _ref17.enabled,
    enabled = _ref17$enabled === void 0 ? true : _ref17$enabled,
    _ref17$escapeKey = _ref17.escapeKey,
    escapeKey = _ref17$escapeKey === void 0 ? true : _ref17$escapeKey,
    _ref17$outsidePress = _ref17.outsidePress,
    outsidePress = _ref17$outsidePress === void 0 ? true : _ref17$outsidePress,
    _ref17$outsidePressEv = _ref17.outsidePressEvent,
    outsidePressEvent = _ref17$outsidePressEv === void 0 ? 'pointerdown' : _ref17$outsidePressEv,
    _ref17$referencePress = _ref17.referencePress,
    referencePress = _ref17$referencePress === void 0 ? false : _ref17$referencePress,
    _ref17$referencePress2 = _ref17.referencePressEvent,
    referencePressEvent = _ref17$referencePress2 === void 0 ? 'pointerdown' : _ref17$referencePress2,
    _ref17$ancestorScroll = _ref17.ancestorScroll,
    ancestorScroll = _ref17$ancestorScroll === void 0 ? false : _ref17$ancestorScroll,
    _ref17$bubbles = _ref17.bubbles,
    bubbles = _ref17$bubbles === void 0 ? true : _ref17$bubbles;
  var tree = useFloatingTree();
  var nested = useFloatingParentNodeId() != null;
  var insideReactTreeRef = React.useRef(false);
  React.useEffect(function () {
    if (!open || !enabled) {
      return;
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        if (!bubbles && tree && getChildren(tree.nodesRef.current, nodeId).length > 0) {
          return;
        }
        events.emit('dismiss', {
          preventScroll: false
        });
        onOpenChange(false);
      }
    }
    function onOutsidePress(event) {
      // Given developers can stop the propagation of the synthetic event,
      // we can only be confident with a positive value.
      var insideReactTree = insideReactTreeRef.current;
      insideReactTreeRef.current = false;
      if (insideReactTree) {
        return;
      }
      var target = getTarget(event); // Check if the click occurred on the scrollbar

      if (isElement(target) && refs.floating.current) {
        var _refs$floating$curren;
        var win = (_refs$floating$curren = refs.floating.current.ownerDocument.defaultView) != null ? _refs$floating$curren : window;
        var canScrollX = target.scrollWidth > target.clientWidth;
        var canScrollY = target.scrollHeight > target.clientHeight;
        var xCond = canScrollY && event.offsetX > target.clientWidth; // In some browsers it is possible to change the <body> (or window)
        // scrollbar to the left side, but is very rare and is difficult to
        // check for. Plus, for modal dialogs with backdrops, it is more
        // important that the backdrop is checked but not so much the window.

        if (canScrollY) {
          var isRTL = win.getComputedStyle(target).direction === 'rtl';
          if (isRTL) {
            xCond = event.offsetX <= target.offsetWidth - target.clientWidth;
          }
        }
        if (xCond || canScrollX && event.offsetY > target.clientHeight) {
          return;
        }
      }
      var targetIsInsideChildren = tree && getChildren(tree.nodesRef.current, nodeId).some(function (node) {
        var _node$context;
        return isEventTargetWithin(event, (_node$context = node.context) == null ? void 0 : _node$context.refs.floating.current);
      });
      if (isEventTargetWithin(event, refs.floating.current) || isEventTargetWithin(event, refs.domReference.current) || targetIsInsideChildren) {
        return;
      }
      if (!bubbles && tree && getChildren(tree.nodesRef.current, nodeId).length > 0) {
        return;
      }
      events.emit('dismiss', nested ? {
        preventScroll: true
      } : false);
      onOpenChange(false);
    }
    function onScroll() {
      onOpenChange(false);
    }
    var doc = getDocument(refs.floating.current);
    escapeKey && doc.addEventListener('keydown', onKeyDown);
    outsidePress && doc.addEventListener(outsidePressEvent, onOutsidePress);
    var ancestors = [];
    if (ancestorScroll) {
      if (isElement(refs.domReference.current)) {
        ancestors = getOverflowAncestors(refs.domReference.current);
      }
      if (isElement(refs.floating.current)) {
        ancestors = ancestors.concat(getOverflowAncestors(refs.floating.current));
      }
      if (!isElement(refs.reference.current) && refs.reference.current &&
      // @ts-expect-error is VirtualElement
      refs.reference.current.contextElement) {
        ancestors = ancestors.concat(
        // @ts-expect-error is VirtualElement
        getOverflowAncestors(refs.reference.current.contextElement));
      }
    } // Ignore the visual viewport for scrolling dismissal (allow pinch-zoom)

    ancestors = ancestors.filter(function (ancestor) {
      var _doc$defaultView;
      return ancestor !== ((_doc$defaultView = doc.defaultView) == null ? void 0 : _doc$defaultView.visualViewport);
    });
    ancestors.forEach(function (ancestor) {
      ancestor.addEventListener('scroll', onScroll, {
        passive: true
      });
    });
    return function () {
      escapeKey && doc.removeEventListener('keydown', onKeyDown);
      outsidePress && doc.removeEventListener(outsidePressEvent, onOutsidePress);
      ancestors.forEach(function (ancestor) {
        ancestor.removeEventListener('scroll', onScroll);
      });
    };
  }, [escapeKey, outsidePress, outsidePressEvent, events, tree, nodeId, open, onOpenChange, ancestorScroll, enabled, bubbles, refs, nested]);
  React.useEffect(function () {
    insideReactTreeRef.current = false;
  }, [outsidePress, outsidePressEvent]);
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    return {
      reference: _defineProperty({}, bubbleHandlerKeys[referencePressEvent], function () {
        if (referencePress) {
          events.emit('dismiss');
          onOpenChange(false);
        }
      }),
      floating: _defineProperty({}, captureHandlerKeys[outsidePressEvent], function () {
        insideReactTreeRef.current = true;
      })
    };
  }, [enabled, events, referencePress, outsidePressEvent, referencePressEvent, onOpenChange]);
};

/**
 * Adds focus event listeners that change the open state, like CSS :focus.
 * @see https://floating-ui.com/docs/useFocus
 */
var useFocus = function useFocus(_ref, _temp) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    dataRef = _ref.dataRef,
    refs = _ref.refs,
    events = _ref.events;
  var _ref18 = _temp === void 0 ? {} : _temp,
    _ref18$enabled = _ref18.enabled,
    enabled = _ref18$enabled === void 0 ? true : _ref18$enabled,
    _ref18$keyboardOnly = _ref18.keyboardOnly,
    keyboardOnly = _ref18$keyboardOnly === void 0 ? true : _ref18$keyboardOnly;
  var pointerTypeRef = React.useRef('');
  var blockFocusRef = React.useRef(false);
  var timeoutRef = React.useRef();
  React.useEffect(function () {
    var _doc$defaultView;
    if (!enabled) {
      return;
    }
    var doc = getDocument(refs.floating.current);
    var win = (_doc$defaultView = doc.defaultView) != null ? _doc$defaultView : window;
    function onBlur() {
      if (!open && isHTMLElement(refs.domReference.current)) {
        refs.domReference.current.blur();
      }
    }
    win.addEventListener('blur', onBlur);
    return function () {
      win.removeEventListener('blur', onBlur);
    };
  }, [refs, open, enabled]);
  React.useEffect(function () {
    if (!enabled) {
      return;
    }
    function onDismiss() {
      blockFocusRef.current = true;
    }
    events.on('dismiss', onDismiss);
    return function () {
      events.off('dismiss', onDismiss);
    };
  }, [events, enabled]);
  React.useEffect(function () {
    return function () {
      clearTimeout(timeoutRef.current);
    };
  }, []);
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    return {
      reference: {
        onPointerDown: function onPointerDown(_ref2) {
          var pointerType = _ref2.pointerType;
          pointerTypeRef.current = pointerType;
          blockFocusRef.current = !!(pointerType && keyboardOnly);
        },
        onPointerLeave: function onPointerLeave() {
          blockFocusRef.current = false;
        },
        onFocus: function onFocus(event) {
          var _dataRef$current$open, _refs$domReference$cu, _dataRef$current$open2;
          if (blockFocusRef.current) {
            return;
          } // Dismiss with click should ignore the subsequent `focus` trigger, but
          // only if the click originated inside the reference element.

          if (event.type === 'focus' && ((_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type) === 'mousedown' && (_refs$domReference$cu = refs.domReference.current) != null && _refs$domReference$cu.contains((_dataRef$current$open2 = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open2.target)) {
            return;
          }
          dataRef.current.openEvent = event.nativeEvent;
          onOpenChange(true);
        },
        onBlur: function onBlur(event) {
          var target = event.relatedTarget; // Wait for the window blur listener to fire.

          timeoutRef.current = setTimeout(function () {
            var _refs$floating$curren, _refs$domReference$cu2;

            // When focusing the reference element (e.g. regular click), then
            // clicking into the floating element, prevent it from hiding.
            // Note: it must be focusable, e.g. `tabindex="-1"`.
            if ((_refs$floating$curren = refs.floating.current) != null && _refs$floating$curren.contains(target) || (_refs$domReference$cu2 = refs.domReference.current) != null && _refs$domReference$cu2.contains(target)) {
              return;
            }
            blockFocusRef.current = false;
            onOpenChange(false);
          });
        }
      }
    };
  }, [enabled, keyboardOnly, refs, dataRef, onOpenChange]);
};
var ARROW_UP = 'ArrowUp';
var ARROW_DOWN = 'ArrowDown';
var ARROW_LEFT = 'ArrowLeft';
var ARROW_RIGHT = 'ArrowRight';
function isDifferentRow(index, cols, prevRow) {
  return Math.floor(index / cols) !== prevRow;
}
function isIndexOutOfBounds(listRef, index) {
  return index < 0 || index >= listRef.current.length;
}
function findNonDisabledIndex(listRef, _temp) {
  var _ref19 = _temp === void 0 ? {} : _temp,
    _ref19$startingIndex = _ref19.startingIndex,
    startingIndex = _ref19$startingIndex === void 0 ? -1 : _ref19$startingIndex,
    _ref19$decrement = _ref19.decrement,
    decrement = _ref19$decrement === void 0 ? false : _ref19$decrement,
    disabledIndices = _ref19.disabledIndices,
    _ref19$amount = _ref19.amount,
    amount = _ref19$amount === void 0 ? 1 : _ref19$amount;
  var list = listRef.current;
  var index = startingIndex;
  do {
    var _list$index, _list$index2;
    index = index + (decrement ? -amount : amount);
  } while (index >= 0 && index <= list.length - 1 && (disabledIndices ? disabledIndices.includes(index) : list[index] == null || ((_list$index = list[index]) == null ? void 0 : _list$index.hasAttribute('disabled')) || ((_list$index2 = list[index]) == null ? void 0 : _list$index2.getAttribute('aria-disabled')) === 'true'));
  return index;
}
function doSwitch(orientation, vertical, horizontal) {
  switch (orientation) {
    case 'vertical':
      return vertical;
    case 'horizontal':
      return horizontal;
    default:
      return vertical || horizontal;
  }
}
function isMainOrientationKey(key, orientation) {
  var vertical = key === ARROW_UP || key === ARROW_DOWN;
  var horizontal = key === ARROW_LEFT || key === ARROW_RIGHT;
  return doSwitch(orientation, vertical, horizontal);
}
function isMainOrientationToEndKey(key, orientation, rtl) {
  var vertical = key === ARROW_DOWN;
  var horizontal = rtl ? key === ARROW_LEFT : key === ARROW_RIGHT;
  return doSwitch(orientation, vertical, horizontal) || key === 'Enter' || key == ' ' || key === '';
}
function isCrossOrientationOpenKey(key, orientation, rtl) {
  var vertical = rtl ? key === ARROW_LEFT : key === ARROW_RIGHT;
  var horizontal = key === ARROW_DOWN;
  return doSwitch(orientation, vertical, horizontal);
}
function isCrossOrientationCloseKey(key, orientation, rtl) {
  var vertical = rtl ? key === ARROW_RIGHT : key === ARROW_LEFT;
  var horizontal = key === ARROW_UP;
  return doSwitch(orientation, vertical, horizontal);
}
function getMinIndex(listRef, disabledIndices) {
  return findNonDisabledIndex(listRef, {
    disabledIndices: disabledIndices
  });
}
function getMaxIndex(listRef, disabledIndices) {
  return findNonDisabledIndex(listRef, {
    decrement: true,
    startingIndex: listRef.current.length,
    disabledIndices: disabledIndices
  });
}

/**
 * Adds focus-managed indexed navigation via arrow keys to a list of items
 * within the floating element.
 * @see https://floating-ui.com/docs/useListNavigation
 */
var useListNavigation = function useListNavigation(_ref, _temp2) {
  var open = _ref.open,
    onOpenChange = _ref.onOpenChange,
    refs = _ref.refs;
  var _ref20 = _temp2 === void 0 ? {
      listRef: {
        current: []
      },
      activeIndex: null,
      onNavigate: function onNavigate() {}
    } : _temp2,
    listRef = _ref20.listRef,
    activeIndex = _ref20.activeIndex,
    _ref20$onNavigate = _ref20.onNavigate,
    unstable_onNavigate = _ref20$onNavigate === void 0 ? function () {} : _ref20$onNavigate,
    _ref20$enabled = _ref20.enabled,
    enabled = _ref20$enabled === void 0 ? true : _ref20$enabled,
    _ref20$selectedIndex = _ref20.selectedIndex,
    selectedIndex = _ref20$selectedIndex === void 0 ? null : _ref20$selectedIndex,
    _ref20$allowEscape = _ref20.allowEscape,
    allowEscape = _ref20$allowEscape === void 0 ? false : _ref20$allowEscape,
    _ref20$loop = _ref20.loop,
    loop = _ref20$loop === void 0 ? false : _ref20$loop,
    _ref20$nested = _ref20.nested,
    nested = _ref20$nested === void 0 ? false : _ref20$nested,
    _ref20$rtl = _ref20.rtl,
    rtl = _ref20$rtl === void 0 ? false : _ref20$rtl,
    _ref20$virtual = _ref20.virtual,
    virtual = _ref20$virtual === void 0 ? false : _ref20$virtual,
    _ref20$focusItemOnOpe = _ref20.focusItemOnOpen,
    focusItemOnOpen = _ref20$focusItemOnOpe === void 0 ? 'auto' : _ref20$focusItemOnOpe,
    _ref20$focusItemOnHov = _ref20.focusItemOnHover,
    focusItemOnHover = _ref20$focusItemOnHov === void 0 ? true : _ref20$focusItemOnHov,
    _ref20$openOnArrowKey = _ref20.openOnArrowKeyDown,
    openOnArrowKeyDown = _ref20$openOnArrowKey === void 0 ? true : _ref20$openOnArrowKey,
    _ref20$disabledIndice = _ref20.disabledIndices,
    disabledIndices = _ref20$disabledIndice === void 0 ? undefined : _ref20$disabledIndice,
    _ref20$orientation = _ref20.orientation,
    orientation = _ref20$orientation === void 0 ? 'vertical' : _ref20$orientation,
    _ref20$cols = _ref20.cols,
    cols = _ref20$cols === void 0 ? 1 : _ref20$cols;
  if (process.env.NODE_ENV !== "production") {
    if (allowEscape) {
      if (!loop) {
        console.warn(['Floating UI: `useListNavigation` looping must be enabled to allow', 'escaping.'].join(' '));
      }
      if (!virtual) {
        console.warn(['Floating UI: `useListNavigation` must be virtual to allow', 'escaping.'].join(' '));
      }
    }
    if (orientation === 'vertical' && cols > 1) {
      console.warn(['Floating UI: In grid list navigation mode (`cols` > 1), the', '`orientation` should be either "horizontal" or "both".'].join(' '));
    }
  }
  var parentId = useFloatingParentNodeId();
  var tree = useFloatingTree();
  var previousOpen = usePrevious(open);
  var onNavigate = useEvent(unstable_onNavigate);
  var previousOnNavigate = useEvent(usePrevious(unstable_onNavigate));
  var focusItemOnOpenRef = React.useRef(focusItemOnOpen);
  var indexRef = React.useRef(selectedIndex != null ? selectedIndex : -1);
  var keyRef = React.useRef(null);
  var disabledIndicesRef = useLatestRef(disabledIndices);
  var blockPointerLeaveRef = React.useRef(false);
  var frameRef = React.useRef(-1);
  var _React$useState9 = React.useState(),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    activeId = _React$useState10[0],
    setActiveId = _React$useState10[1];
  var focusItem = React.useCallback(function (listRef, indexRef) {
    // `mousedown` clicks occur before `focus`, so the button will steal the
    // focus unless we wait a frame.
    frameRef.current = requestAnimationFrame(function () {
      if (virtual) {
        var _listRef$current$inde;
        setActiveId((_listRef$current$inde = listRef.current[indexRef.current]) == null ? void 0 : _listRef$current$inde.id);
      } else {
        var _listRef$current$inde2;
        (_listRef$current$inde2 = listRef.current[indexRef.current]) == null ? void 0 : _listRef$current$inde2.focus({
          preventScroll: true
        });
      }
    });
  }, [virtual]); // Sync `selectedIndex` to be the `activeIndex` upon opening the floating
  // element. Also, reset `activeIndex` upon closing the floating element.

  index(function () {
    if (!enabled) {
      return;
    }
    if (!previousOpen && open && focusItemOnOpenRef.current && selectedIndex != null) {
      onNavigate(selectedIndex);
    } // Unset `activeIndex`. Since the user can specify `onNavigate`
    // conditionally (onNavigate: open ? setActiveIndex : setSelectedIndex)
    // we store and call the previous function

    if (previousOpen && !open) {
      cancelAnimationFrame(frameRef.current);
      indexRef.current = -1;
      previousOnNavigate(null);
    }
  }, [open, previousOpen, selectedIndex, listRef, focusItem, enabled, onNavigate, previousOnNavigate]); // Sync `activeIndex` to be the focused item while the floating element is
  // open.

  index(function () {
    if (!enabled) {
      return;
    }
    if (open) {
      if (activeIndex == null) {
        if (selectedIndex != null) {
          return;
        } // Reset while the floating element was open (e.g. the list changed).

        if (previousOpen) {
          indexRef.current = -1;
          focusItem(listRef, indexRef);
        } // Initial sync

        if (!previousOpen && focusItemOnOpenRef.current && (keyRef.current != null || focusItemOnOpenRef.current === true && keyRef.current == null)) {
          indexRef.current = keyRef.current == null || isMainOrientationToEndKey(keyRef.current, orientation, rtl) || nested ? getMinIndex(listRef, disabledIndicesRef.current) : getMaxIndex(listRef, disabledIndicesRef.current);
          onNavigate(indexRef.current);
          focusItem(listRef, indexRef);
        }
      } else if (!isIndexOutOfBounds(listRef, activeIndex)) {
        indexRef.current = activeIndex;
        focusItem(listRef, indexRef);
      }
    }
  }, [open, previousOpen, activeIndex, selectedIndex, nested, listRef, onNavigate, focusItem, enabled, allowEscape, orientation, rtl, virtual, disabledIndicesRef]); // Ensure the parent floating element has focus when a nested child closes
  // to allow arrow key navigation to work after the pointer leaves the child.

  index(function () {
    if (!enabled) {
      return;
    }
    if (!open && previousOpen) {
      var _tree$nodesRef$curren, _tree$nodesRef$curren2;
      var parentFloating = tree == null ? void 0 : (_tree$nodesRef$curren = tree.nodesRef.current.find(function (node) {
        return node.id === parentId;
      })) == null ? void 0 : (_tree$nodesRef$curren2 = _tree$nodesRef$curren.context) == null ? void 0 : _tree$nodesRef$curren2.refs.floating.current;
      if (parentFloating && !parentFloating.contains(activeElement(getDocument(parentFloating)))) {
        parentFloating.focus({
          preventScroll: true
        });
      }
    }
  }, [enabled, open, previousOpen, tree, parentId]);
  index(function () {
    keyRef.current = null;
  });
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    var disabledIndices = disabledIndicesRef.current;
    function _onKeyDown(event) {
      blockPointerLeaveRef.current = true;
      if (nested && isCrossOrientationCloseKey(event.key, orientation, rtl)) {
        stopEvent(event);
        onOpenChange(false);
        if (isHTMLElement(refs.domReference.current)) {
          refs.domReference.current.focus();
        }
        return;
      }
      var currentIndex = indexRef.current;
      var minIndex = getMinIndex(listRef, disabledIndices);
      var maxIndex = getMaxIndex(listRef, disabledIndices);
      if (event.key === 'Home') {
        indexRef.current = minIndex;
        onNavigate(indexRef.current);
      }
      if (event.key === 'End') {
        indexRef.current = maxIndex;
        onNavigate(indexRef.current);
      } // Grid navigation

      if (cols > 1) {
        var prevIndex = indexRef.current;
        if (event.key === ARROW_UP) {
          stopEvent(event);
          if (prevIndex === -1) {
            indexRef.current = maxIndex;
          } else {
            indexRef.current = findNonDisabledIndex(listRef, {
              startingIndex: prevIndex,
              amount: cols,
              decrement: true,
              disabledIndices: disabledIndices
            });
            if (loop && (prevIndex - cols < minIndex || indexRef.current < 0)) {
              var col = prevIndex % cols;
              var maxCol = maxIndex % cols;
              var _offset = maxIndex - (maxCol - col);
              if (maxCol === col) {
                indexRef.current = maxIndex;
              } else {
                indexRef.current = maxCol > col ? _offset : _offset - cols;
              }
            }
          }
          if (isIndexOutOfBounds(listRef, indexRef.current)) {
            indexRef.current = prevIndex;
          }
          onNavigate(indexRef.current);
        }
        if (event.key === ARROW_DOWN) {
          stopEvent(event);
          if (prevIndex === -1) {
            indexRef.current = minIndex;
          } else {
            indexRef.current = findNonDisabledIndex(listRef, {
              startingIndex: prevIndex,
              amount: cols,
              disabledIndices: disabledIndices
            });
            if (loop && prevIndex + cols > maxIndex) {
              indexRef.current = findNonDisabledIndex(listRef, {
                startingIndex: prevIndex % cols - cols,
                amount: cols,
                disabledIndices: disabledIndices
              });
            }
          }
          if (isIndexOutOfBounds(listRef, indexRef.current)) {
            indexRef.current = prevIndex;
          }
          onNavigate(indexRef.current);
        } // Remains on the same row/column

        if (orientation === 'both') {
          var prevRow = Math.floor(prevIndex / cols);
          if (event.key === ARROW_RIGHT) {
            stopEvent(event);
            if (prevIndex % cols !== cols - 1) {
              indexRef.current = findNonDisabledIndex(listRef, {
                startingIndex: prevIndex,
                disabledIndices: disabledIndices
              });
              if (loop && isDifferentRow(indexRef.current, cols, prevRow)) {
                indexRef.current = findNonDisabledIndex(listRef, {
                  startingIndex: prevIndex - prevIndex % cols - 1,
                  disabledIndices: disabledIndices
                });
              }
            } else if (loop) {
              indexRef.current = findNonDisabledIndex(listRef, {
                startingIndex: prevIndex - prevIndex % cols - 1,
                disabledIndices: disabledIndices
              });
            }
            if (isDifferentRow(indexRef.current, cols, prevRow)) {
              indexRef.current = prevIndex;
            }
          }
          if (event.key === ARROW_LEFT) {
            stopEvent(event);
            if (prevIndex % cols !== 0) {
              indexRef.current = findNonDisabledIndex(listRef, {
                startingIndex: prevIndex,
                disabledIndices: disabledIndices,
                decrement: true
              });
              if (loop && isDifferentRow(indexRef.current, cols, prevRow)) {
                indexRef.current = findNonDisabledIndex(listRef, {
                  startingIndex: prevIndex + (cols - prevIndex % cols),
                  decrement: true,
                  disabledIndices: disabledIndices
                });
              }
            } else if (loop) {
              indexRef.current = findNonDisabledIndex(listRef, {
                startingIndex: prevIndex + (cols - prevIndex % cols),
                decrement: true,
                disabledIndices: disabledIndices
              });
            }
            if (isDifferentRow(indexRef.current, cols, prevRow)) {
              indexRef.current = prevIndex;
            }
          }
          var lastRow = Math.floor(maxIndex / cols) === prevRow;
          if (isIndexOutOfBounds(listRef, indexRef.current)) {
            if (loop && lastRow) {
              indexRef.current = event.key === ARROW_LEFT ? maxIndex : findNonDisabledIndex(listRef, {
                startingIndex: prevIndex - prevIndex % cols - 1,
                disabledIndices: disabledIndices
              });
            } else {
              indexRef.current = prevIndex;
            }
          }
          onNavigate(indexRef.current);
          return;
        }
      }
      if (isMainOrientationKey(event.key, orientation)) {
        stopEvent(event); // Reset the index if no item is focused.

        if (open && !virtual && activeElement(event.currentTarget.ownerDocument) === event.currentTarget) {
          indexRef.current = isMainOrientationToEndKey(event.key, orientation, rtl) ? minIndex : maxIndex;
          onNavigate(indexRef.current);
          return;
        }
        if (isMainOrientationToEndKey(event.key, orientation, rtl)) {
          if (loop) {
            indexRef.current = currentIndex >= maxIndex ? allowEscape && currentIndex !== listRef.current.length ? -1 : minIndex : findNonDisabledIndex(listRef, {
              startingIndex: currentIndex,
              disabledIndices: disabledIndices
            });
          } else {
            indexRef.current = Math.min(maxIndex, findNonDisabledIndex(listRef, {
              startingIndex: currentIndex,
              disabledIndices: disabledIndices
            }));
          }
        } else {
          if (loop) {
            indexRef.current = currentIndex <= minIndex ? allowEscape && currentIndex !== -1 ? listRef.current.length : maxIndex : findNonDisabledIndex(listRef, {
              startingIndex: currentIndex,
              decrement: true,
              disabledIndices: disabledIndices
            });
          } else {
            indexRef.current = Math.max(minIndex, findNonDisabledIndex(listRef, {
              startingIndex: currentIndex,
              decrement: true,
              disabledIndices: disabledIndices
            }));
          }
        }
        if (isIndexOutOfBounds(listRef, indexRef.current)) {
          onNavigate(null);
        } else {
          onNavigate(indexRef.current);
        }
      }
    }
    return {
      reference: _objectSpread(_objectSpread({}, virtual && open && activeIndex != null && {
        'aria-activedescendant': activeId
      }), {}, {
        onKeyDown: function onKeyDown(event) {
          blockPointerLeaveRef.current = true;
          if (virtual && open) {
            return _onKeyDown(event);
          }
          var isNavigationKey = event.key.indexOf('Arrow') === 0 || event.key === 'Enter' || event.key === ' ' || event.key === '';
          if (isNavigationKey) {
            keyRef.current = event.key;
          }
          if (nested) {
            if (isCrossOrientationOpenKey(event.key, orientation, rtl)) {
              stopEvent(event);
              if (open) {
                indexRef.current = getMinIndex(listRef, disabledIndices);
                onNavigate(indexRef.current);
              } else {
                onOpenChange(true);
              }
            }
            return;
          }
          if (isMainOrientationKey(event.key, orientation)) {
            if (selectedIndex != null) {
              indexRef.current = selectedIndex;
            }
            stopEvent(event);
            if (!open && openOnArrowKeyDown) {
              onOpenChange(true);
            } else {
              _onKeyDown(event);
            }
            if (open) {
              onNavigate(indexRef.current);
            }
          }
        }
      }),
      floating: _objectSpread(_objectSpread({
        'aria-orientation': orientation === 'both' ? undefined : orientation
      }, virtual && activeIndex != null && {
        'aria-activedescendant': activeId
      }), {}, {
        onKeyDown: _onKeyDown,
        onPointerMove: function onPointerMove() {
          blockPointerLeaveRef.current = false;
        }
      }),
      item: _objectSpread({
        onFocus: function onFocus(_ref2) {
          var currentTarget = _ref2.currentTarget;
          var index = listRef.current.indexOf(currentTarget);
          if (index !== -1) {
            onNavigate(index);
          }
        },
        onClick: function onClick(_ref3) {
          var currentTarget = _ref3.currentTarget;
          return currentTarget.focus({
            preventScroll: true
          });
        }
      }, focusItemOnHover && {
        onMouseMove: function onMouseMove(_ref4) {
          var currentTarget = _ref4.currentTarget;
          var target = currentTarget;
          if (target) {
            var _index = listRef.current.indexOf(target);
            if (_index !== -1) {
              onNavigate(_index);
            }
          }
        },
        onPointerLeave: function onPointerLeave() {
          if (!blockPointerLeaveRef.current) {
            indexRef.current = -1;
            focusItem(listRef, indexRef);
            onNavigate(null);
            if (!virtual) {
              requestAnimationFrame(function () {
                var _refs$floating$curren;
                (_refs$floating$curren = refs.floating.current) == null ? void 0 : _refs$floating$curren.focus({
                  preventScroll: true
                });
              });
            }
          }
        }
      })
    };
  }, [activeId, disabledIndicesRef, listRef, enabled, orientation, rtl, virtual, open, activeIndex, nested, selectedIndex, openOnArrowKeyDown, focusItemOnHover, allowEscape, cols, loop, refs, focusItem, onNavigate, onOpenChange]);
};

/**
 * Provides a matching callback that can be used to focus an item as the user
 * types, often used in tandem with `useListNavigation()`.
 * @see https://floating-ui.com/docs/useTypeahead
 */
var useTypeahead = function useTypeahead(_ref, _temp) {
  var _ref2;
  var open = _ref.open,
    dataRef = _ref.dataRef;
  var _ref21 = _temp === void 0 ? {
      listRef: {
        current: []
      },
      activeIndex: null
    } : _temp,
    listRef = _ref21.listRef,
    activeIndex = _ref21.activeIndex,
    _ref21$onMatch = _ref21.onMatch,
    unstable_onMatch = _ref21$onMatch === void 0 ? function () {} : _ref21$onMatch,
    _ref21$enabled = _ref21.enabled,
    enabled = _ref21$enabled === void 0 ? true : _ref21$enabled,
    _ref21$findMatch = _ref21.findMatch,
    findMatch = _ref21$findMatch === void 0 ? null : _ref21$findMatch,
    _ref21$resetMs = _ref21.resetMs,
    resetMs = _ref21$resetMs === void 0 ? 1000 : _ref21$resetMs,
    _ref21$ignoreKeys = _ref21.ignoreKeys,
    ignoreKeys = _ref21$ignoreKeys === void 0 ? [] : _ref21$ignoreKeys,
    _ref21$selectedIndex = _ref21.selectedIndex,
    selectedIndex = _ref21$selectedIndex === void 0 ? null : _ref21$selectedIndex;
  var timeoutIdRef = React.useRef();
  var stringRef = React.useRef('');
  var prevIndexRef = React.useRef((_ref2 = selectedIndex != null ? selectedIndex : activeIndex) != null ? _ref2 : -1);
  var matchIndexRef = React.useRef(null);
  var onMatch = useEvent(unstable_onMatch);
  var findMatchRef = useLatestRef(findMatch);
  var ignoreKeysRef = useLatestRef(ignoreKeys);
  index(function () {
    if (open) {
      clearTimeout(timeoutIdRef.current);
      matchIndexRef.current = null;
      stringRef.current = '';
    }
  }, [open]);
  index(function () {
    // Sync arrow key navigation but not typeahead navigation
    if (open && stringRef.current === '') {
      var _ref3;
      prevIndexRef.current = (_ref3 = selectedIndex != null ? selectedIndex : activeIndex) != null ? _ref3 : -1;
    }
  }, [open, selectedIndex, activeIndex]);
  return React.useMemo(function () {
    if (!enabled) {
      return {};
    }
    function onKeyDown(event) {
      // Correctly scope nested non-portalled floating elements. Since the nested
      // floating element is inside of the another, we find the closest role
      // that indicates the floating element scope.
      var target = getTarget(event.nativeEvent);
      if (isElement(target) && (activeElement(getDocument(target)) !== event.currentTarget ? target.closest('[role="dialog"],[role="menu"],[role="listbox"],[role="tree"],[role="grid"]') !== event.currentTarget : false)) {
        return;
      }
      if (stringRef.current.length > 0 && stringRef.current[0] !== ' ') {
        dataRef.current.typing = true;
        if (event.key === ' ') {
          stopEvent(event);
        }
      }
      var listContent = listRef.current;
      if (listContent == null || ignoreKeysRef.current.includes(event.key) ||
      // Character key
      event.key.length !== 1 ||
      // Modifier key
      event.ctrlKey || event.metaKey || event.altKey) {
        return;
      } // Bail out if the list contains a word like "llama" or "aaron". TODO:
      // allow it in this case, too.

      var allowRapidSuccessionOfFirstLetter = listContent.every(function (text) {
        var _text$, _text$2;
        return text ? ((_text$ = text[0]) == null ? void 0 : _text$.toLocaleLowerCase()) !== ((_text$2 = text[1]) == null ? void 0 : _text$2.toLocaleLowerCase()) : true;
      }); // Allows the user to cycle through items that start with the same letter
      // in rapid succession

      if (allowRapidSuccessionOfFirstLetter && stringRef.current === event.key) {
        stringRef.current = '';
        prevIndexRef.current = matchIndexRef.current;
      }
      stringRef.current += event.key;
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(function () {
        stringRef.current = '';
        prevIndexRef.current = matchIndexRef.current;
        dataRef.current.typing = false;
      }, resetMs);
      var prevIndex = prevIndexRef.current;
      var orderedList = [].concat(_toConsumableArray(listContent.slice((prevIndex != null ? prevIndex : 0) + 1)), _toConsumableArray(listContent.slice(0, (prevIndex != null ? prevIndex : 0) + 1)));
      var str = findMatchRef.current ? findMatchRef.current(orderedList, stringRef.current) : orderedList.find(function (text) {
        return (text == null ? void 0 : text.toLocaleLowerCase().indexOf(stringRef.current.toLocaleLowerCase())) === 0;
      });
      var index = str ? listContent.indexOf(str) : -1;
      if (index !== -1) {
        onMatch(index);
        matchIndexRef.current = index;
      }
    }
    return {
      reference: {
        onKeyDown: onKeyDown
      },
      floating: {
        onKeyDown: onKeyDown
      }
    };
  }, [enabled, dataRef, listRef, resetMs, ignoreKeysRef, findMatchRef, onMatch]);
};
export { FloatingDelayGroup, FloatingFocusManager, FloatingNode, FloatingOverlay, FloatingPortal, FloatingTree, inner, safePolygon, useClick, useDelayGroup, useDelayGroupContext, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingPortalNode, useFloatingTree, useFocus, useHover, useId, useInnerOffset, useInteractions, useListNavigation, useRole, useTypeahead };